
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAssistantNautique } from "@/hooks/useAssistantNautique";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Anchor, Send, Crown, User, Bot, Trash2, CheckSquare, Square, ChevronDown, ChevronUp, RefreshCw, ArrowRight, Paperclip, X } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { useProAccess } from "@/hooks/useProAccess";
import { toast } from "@/components/ui/use-toast";


const FREE_QUESTIONS_LIMIT = 1;
const PREMIUM_QUESTIONS_LIMIT = 20;

// Fonction pour convertir le Markdown simplifié en HTML
const renderMarkdown = (text: string): string => {
  return text
    .replace(/\[\d+\]/g, '') // Enlever les références [1], [2], etc.
    .replace(/^### (.+)$/gm, '<h3 class="font-bold text-base mt-3 mb-1">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-bold text-lg mt-3 mb-1">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="font-bold text-xl mt-3 mb-1">$1</h1>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul class="my-1 space-y-0.5">$&</ul>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>')
    .trim();
};

const AssistantNautiquePage = () => {
  const navigate = useNavigate();
  const { loading, questions, poserQuestion, chargerHistorique, obtenirUsageQuotidien, supprimerConversations, isDemoAccount } = useAssistantNautique();
  const { isProAccount, loading: proLoading } = useProAccess();
  const [question, setQuestion] = useState("");
  const [currentUsage, setCurrentUsage] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [collapsedConversations, setCollapsedConversations] = useState<string[]>([]);
  const [replyText, setReplyText] = useState("");
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compress image client-side: max 1280px, JPEG quality 0.8
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const MAX_DIM = 1280;
          let { width, height } = img;
          if (width > height && width > MAX_DIM) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else if (height > MAX_DIM) {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas non supporté"));
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error("Image illisible"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Lecture du fichier échouée"));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Format non supporté",
        description: "Formats acceptés : JPEG, PNG, WebP.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    // 10 Mo brut max avant compression
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Image trop volumineuse",
        description: "Taille maximum : 10 Mo.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    setImageProcessing(true);
    try {
      const compressed = await compressImage(file);
      // Vérifier la taille après compression (~ 4 Mo en base64 = ~3 Mo binaire)
      if (compressed.length > 5_500_000) {
        toast({
          title: "Image encore trop lourde après compression",
          description: "Essayez une image plus petite ou de moins bonne qualité.",
          variant: "destructive",
        });
      } else {
        setImageBase64(compressed);
      }
    } catch (err: any) {
      toast({
        title: "Erreur image",
        description: err?.message || "Impossible de traiter l'image.",
        variant: "destructive",
      });
    } finally {
      setImageProcessing(false);
      e.target.value = "";
    }
  };

  const removeImage = () => setImageBase64(null);

  React.useEffect(() => {
    chargerHistorique();
  }, []);

  React.useEffect(() => {
    // Important: on attend de connaître le statut Premium, sinon on calcule le quota avec la limite gratuite (1)
    if (!proLoading) {
      checkCurrentUsage();
    }
  }, [proLoading, isProAccount]);

  // Initialiser les anciennes conversations comme réduites, garder la dernière ouverte
  React.useEffect(() => {
    if (questions.length > 0) {
      // La première question (index 0) est la plus récente, on la garde ouverte
      // Les autres sont repliées
      setCollapsedConversations(questions.slice(1).map(q => q.id));
    }
  }, [questions]);

  const checkCurrentUsage = async () => {
    const usage = await obtenirUsageQuotidien();
    const limit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;

    // Supporte les 2 formats (ancien: number / nouveau: { questions_count, questions_restantes })
    const usageCount = typeof usage === 'number'
      ? usage
      : (usage && typeof usage === 'object' && 'questions_count' in usage)
        ? (usage as any).questions_count
        : 0;

    setCurrentUsage(usageCount);
    // Toujours recalculer (et donc réinitialiser) l'état de blocage
    setLimitReached(usageCount >= limit);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // On autorise l'envoi si question OU image présente
    if (!question.trim() && !imageBase64) return;

    const limit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;
    const questionText = question.trim() || "Analyse cette image dans un contexte nautique.";
    const result = await poserQuestion(questionText, undefined, imageBase64 || undefined);
    if (result.limitReached) {
      setLimitReached(true);
      setCurrentUsage(limit);
    }
    setQuestion("");
    setImageBase64(null);
    await checkCurrentUsage();
  };

  const handleSelectConversation = (conversationId: string, checked: boolean) => {
    if (checked) {
      setSelectedConversations(prev => [...prev, conversationId]);
    } else {
      setSelectedConversations(prev => prev.filter(id => id !== conversationId));
    }
  };

  const handleSelectAll = () => {
    if (selectedConversations.length === questions.length) {
      setSelectedConversations([]);
    } else {
      setSelectedConversations(questions.map(q => q.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedConversations.length === 0) return;
    
    const success = await supprimerConversations(selectedConversations);
    if (success) {
      setSelectedConversations([]);
      setIsSelectionMode(false);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedConversations([]);
  };

  const toggleConversationCollapse = (conversationId: string) => {
    setCollapsedConversations(prev => 
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };

  const handleRenewSelected = async () => {
    if (selectedConversations.length === 0) return;
    
    const limit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;
    // Trouver la première question sélectionnée et la reposer
    const firstSelectedQuestion = questions.find(q => selectedConversations.includes(q.id));
    if (firstSelectedQuestion && canAskQuestion) {
      const result = await poserQuestion(firstSelectedQuestion.question);
      if (result.limitReached) {
        setLimitReached(true);
        setCurrentUsage(limit);
      }
      await checkCurrentUsage();
      setSelectedConversations([]);
      setIsSelectionMode(false);
    }
  };

  const handleRenewSingle = async (questionText: string) => {
    if (!canAskQuestion) return;
    const limit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;
    const result = await poserQuestion(questionText);
    if (result.limitReached) {
      setLimitReached(true);
      setCurrentUsage(limit);
      return;
    }
    await checkCurrentUsage();
  };

  // Continuer une conversation (follow-up)
  const handleFollowUp = async (conversationId: string) => {
    if (!replyText.trim() || !canAskQuestion) return;
    
    // Trouver la conversation d'origine pour construire l'historique
    const originalConversation = questions.find(q => q.id === conversationId);
    if (!originalConversation) return;

    const limit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;
    
    // Construire l'historique de la conversation
    const conversationHistory: Array<{ role: string; content: string }> = [
      { role: 'user', content: originalConversation.question },
    ];
    if (originalConversation.reponse) {
      conversationHistory.push({ role: 'assistant', content: originalConversation.reponse });
    }

    const result = await poserQuestion(replyText, conversationHistory);
    if (result.limitReached) {
      setLimitReached(true);
      setCurrentUsage(limit);
    }
    setReplyText("");
    setReplyingToId(null);
    await checkCurrentUsage();
  };

  const currentLimit = isProAccount ? PREMIUM_QUESTIONS_LIMIT : FREE_QUESTIONS_LIMIT;
  const canAskQuestion = isDemoAccount || (currentUsage < currentLimit && !limitReached);

  return (
    <MainLayout>
      <div className="w-full max-w-4xl mx-auto px-4 lg:px-0 space-y-6 overflow-x-hidden">
        <div className="flex items-center justify-center">
          <div className="text-center w-full">
            <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
              <Anchor className="h-6 w-6 lg:h-8 lg:w-8 text-nautic" />
              <h1 className="text-2xl lg:text-3xl font-bold text-nautic">Assistant Nautique</h1>
              {!isProAccount && <Crown className="h-5 w-5 lg:h-6 lg:w-6 text-nautic-gold" />}
            </div>
            <p className="text-gray-600 text-sm lg:text-base">
              Votre expert nautique personnel pour toutes vos questions
            </p>
            {isDemoAccount ? (
              <div className="mt-4 flex justify-center">
                <Badge
                  variant="outline"
                  className="text-nautic-gold border-nautic-gold text-xs lg:text-sm"
                >
                  <Crown className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                  Accès illimité
                </Badge>
              </div>
            ) : isProAccount ? (
              <div className="mt-4 flex justify-center">
                <Badge
                  variant="outline"
                  className="text-nautic border-nautic text-xs lg:text-sm max-w-full whitespace-normal flex-wrap justify-center text-center"
                >
                  <Crown className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                  Compte Premium : {currentUsage}/{PREMIUM_QUESTIONS_LIMIT} questions par jour
                </Badge>
              </div>
            ) : (
              <div className="mt-4 flex justify-center">
                <Badge
                  variant="outline"
                  className="text-nautic-gold border-nautic-gold text-xs lg:text-sm max-w-full whitespace-normal flex-wrap justify-center text-center"
                >
                  <Crown className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                  Compte gratuit : {currentUsage}/{FREE_QUESTIONS_LIMIT} question par jour
                </Badge>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Formulaire de question */}
          <Card>
            <CardContent className="p-4 lg:p-6">
              {/* Aperçu de l'image jointe */}
              {imageBase64 && (
                <div className="mb-3 inline-flex items-start gap-2 p-2 border border-nautic/20 rounded-lg bg-muted/30 relative">
                  <img
                    src={imageBase64}
                    alt="Aperçu"
                    className="h-20 w-20 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:opacity-90"
                    aria-label="Retirer l'image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="text-xs text-muted-foreground self-center">
                    Image prête à être envoyée
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || !canAskQuestion || imageProcessing}
                  className="border-nautic text-nautic hover:bg-nautic hover:text-white"
                  title="Joindre une photo"
                >
                  <Paperclip className="h-4 w-4" />
                  <span className="lg:hidden ml-1">{imageProcessing ? "Traitement..." : "Photo"}</span>
                </Button>
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={
                    canAskQuestion
                      ? imageBase64
                        ? "Décrivez ce que vous voulez savoir sur l'image (optionnel)..."
                        : "Posez votre question nautique..."
                      : "Limite de questions atteinte pour aujourd'hui"
                  }
                  disabled={loading || !canAskQuestion}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  disabled={loading || imageProcessing || (!question.trim() && !imageBase64) || !canAskQuestion}
                  className="bg-nautic hover:bg-nautic-light text-white w-full lg:w-auto"
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-1 lg:mr-0" />
                  <span className="lg:hidden">Envoyer</span>
                </Button>
              </form>
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-center">
                L'IA peut faire des erreurs. Vérifiez toujours vos équipements et météo manuellement.
              </p>
              
              {!canAskQuestion && (
                <div className="mt-4 p-4 lg:p-6 bg-gradient-to-r from-nautic-gold/20 to-nautic-gold/10 border-2 border-nautic-gold rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-nautic-gold rounded-full">
                      <Crown className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-nautic">Quota journalier atteint !</h3>
                      <p className="text-sm text-gray-600">
                        {isProAccount 
                          ? `Vous avez utilisé vos ${PREMIUM_QUESTIONS_LIMIT} questions Premium aujourd'hui.`
                          : "Vous avez utilisé votre question gratuite aujourd'hui."}
                      </p>
                    </div>
                  </div>
                  {!isProAccount && (
                    <>
                      <p className="text-sm lg:text-base text-gray-700 mb-4">
                        Passez à l'<span className="font-semibold text-nautic-gold">abonnement Premium</span> pour bénéficier de 
                        <span className="font-semibold"> {PREMIUM_QUESTIONS_LIMIT} questions par jour</span> avec votre assistant nautique personnel.
                      </p>
                      <Button
                        onClick={() => navigate('/account')}
                        className="w-full bg-nautic-gold hover:bg-nautic-gold/90 text-white font-semibold"
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        Passer à l'abonnement Premium
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* En-tête des conversations avec boutons de gestion */}
          {questions.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-nautic">Historique des conversations</h2>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectionMode}
                  className="text-nautic border-nautic hover:bg-nautic hover:text-white whitespace-normal h-auto text-center"
                >
                  {isSelectionMode ? (
                    <>
                      <Square className="h-4 w-4 mr-1" />
                      Annuler
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-4 w-4 mr-1" />
                      Sélectionner
                    </>
                  )}
                </Button>
                
                {isSelectionMode && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                      className="text-nautic border-nautic hover:bg-nautic hover:text-white whitespace-normal h-auto text-center"
                    >
                      {selectedConversations.length === questions.length ? "Désélectionner tout" : "Tout sélectionner"}
                    </Button>
                    
                    {selectedConversations.length > 0 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRenewSelected}
                          disabled={loading || !canAskQuestion || selectedConversations.length !== 1}
                          className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                          title={selectedConversations.length !== 1 ? "Sélectionnez une seule conversation" : "Reposer cette question"}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Renouveler
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Supprimer ({selectedConversations.length})
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir supprimer {selectedConversations.length} conversation(s) ? 
                                Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={handleDeleteSelected}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Historique des questions */}
          <div className="space-y-4">
            {questions.length === 0 ? (
              <Card>
                <CardContent className="p-6 lg:p-8 text-center">
                  <Anchor className="h-10 w-10 lg:h-12 lg:w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Aucune question posée
                  </h3>
                  <p className="text-gray-500 text-sm lg:text-base">
                    Commencez par poser votre première question à l'assistant nautique
                  </p>
                </CardContent>
              </Card>
            ) : (
              questions.map((qa) => (
                <div key={qa.id} className="space-y-3">
                  {isSelectionMode && (
                    <div className="flex items-center justify-end mb-2">
                      <Checkbox
                        checked={selectedConversations.includes(qa.id)}
                        onCheckedChange={(checked) => handleSelectConversation(qa.id, checked as boolean)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">Sélectionner cette conversation</span>
                    </div>
                  )}
                  
                  {/* Question de l'utilisateur */}
                  <div className="flex justify-end w-full">
                    <div className="max-w-[85%] sm:max-w-[80%] lg:max-w-3xl bg-nautic text-white rounded-lg p-3 lg:p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <User className="h-3 w-3 lg:h-4 lg:w-4 flex-shrink-0" />
                          <span className="font-semibold text-xs lg:text-sm truncate">
                            {collapsedConversations.includes(qa.id) ? qa.question : "Vous"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isSelectionMode && (
                            <button
                              onClick={() => handleRenewSingle(qa.question)}
                              disabled={loading || !canAskQuestion}
                              className="hover:bg-white/10 rounded p-1 transition-colors disabled:opacity-50"
                              title="Reposer cette question"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => toggleConversationCollapse(qa.id)}
                            className="hover:bg-white/10 rounded p-1 transition-colors"
                          >
                            {collapsedConversations.includes(qa.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronUp className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      {!collapsedConversations.includes(qa.id) && (
                        <p className="text-sm lg:text-base break-words">{qa.question}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Réponse de l'assistant */}
                  {qa.reponse && !collapsedConversations.includes(qa.id) && (
                    <div className="flex justify-start w-full">
                      <div className="max-w-[85%] sm:max-w-[80%] lg:max-w-3xl bg-gray-100 rounded-lg p-3 lg:p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="h-3 w-3 lg:h-4 lg:w-4 text-nautic flex-shrink-0" />
                          <span className="font-semibold text-xs lg:text-sm text-nautic">Assistant Nautique</span>
                        </div>
                        <div 
                          className="prose prose-sm max-w-none text-sm lg:text-base break-words"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(qa.reponse) }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Bouton et champ de réponse pour continuer la conversation */}
                  {qa.reponse && !collapsedConversations.includes(qa.id) && !isSelectionMode && (
                    <div className="flex justify-start w-full pl-4">
                      {replyingToId === qa.id ? (
                        <div className="max-w-[85%] sm:max-w-[80%] lg:max-w-3xl w-full">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleFollowUp(qa.id);
                            }}
                            className="flex gap-2"
                          >
                            <Input
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Continuez la conversation..."
                              disabled={loading || !canAskQuestion}
                              className="flex-1"
                              autoFocus
                            />
                            <Button
                              type="submit"
                              disabled={loading || !replyText.trim() || !canAskQuestion}
                              size="sm"
                              className="bg-nautic hover:bg-nautic-light text-white"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => { setReplyingToId(null); setReplyText(""); }}
                            >
                              ✕
                            </Button>
                          </form>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setReplyingToId(qa.id)}
                          disabled={!canAskQuestion || loading}
                          className="text-nautic hover:text-nautic-light text-xs gap-1"
                        >
                          <Send className="h-3 w-3" />
                          Continuer la conversation
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AssistantNautiquePage;
